import { AsyncSelect, SelectInstance, SelectOption } from '@cimplico/components';
import { Ref, useEffect, useMemo, useState } from 'react';
import { disableCreateWorkpaperHint } from '../../features/new-workpaper/utils';
import { useFirmContext } from '../../providers/FirmProvider';
import { useEntities } from '../../queries/use-entities';
import { Entity } from '../../types/entity';

type EntitySelectProps = {
    defaultEntityIds?: string[];
    title?: string;
    isOptional?: boolean;
    isMulti?: boolean;
    placeholder?: string;
    form?: string;
    errorMessage?: string;
    workpaperForm?: boolean;
    isDisabled?: boolean;
    onChange?: (entities: Entity[]) => void;
    innerRef?: Ref<SelectInstance>;
};

function EntitySelect({
    defaultEntityIds,
    title,
    isMulti = false,
    isOptional = false,
    placeholder,
    form,
    errorMessage,
    workpaperForm = false,
    isDisabled,
    onChange,
    innerRef,
}: EntitySelectProps): JSX.Element {
    const [entitySearch, setEntitySearch] = useState('');
    const [selected, setSelected] = useState<string[] | undefined>(undefined);
    const entityQuery = useEntities({ page: 1, search: entitySearch, sort: '', filter: 'isArchived=false' });
    const firm = useFirmContext();
    const { subscription } = firm;

    useEffect(() => {
        const entities = entityQuery.data?.entities.filter((e) => defaultEntityIds?.includes(e.id));

        if (!selected && entities && entities.length > 0 && defaultEntityIds && defaultEntityIds.length > 0) {
            // Filter entities that can't have new workpaper created
            const disabledOptions = entities.filter((e) => !!disableCreateWorkpaperHint(firm, e, subscription));

            // Set selected entities to exclude the disabled ones
            setSelected(defaultEntityIds?.filter((e) => !disabledOptions.find((ent) => ent.id === e)));
        }
    }, [defaultEntityIds, entityQuery.data?.entities, selected, subscription, firm]);

    function handleEntitySelect(options: SelectOption[]): void {
        setSelected(options.map((o) => o.value.toString()));
    }

    const entities = useMemo(() => {
        return (
            entityQuery.data?.entities?.map((e) => {
                const entityBelongsToGroup = workpaperForm === false && e.groups.length !== 0;
                const disableCreate = !!disableCreateWorkpaperHint(firm, e, subscription);

                return {
                    value: e.id,
                    label: e.name,
                    isDisabled: entityBelongsToGroup || (workpaperForm === true && disableCreate),
                    isSelected: (selected || []).includes(e.id),
                    disabledHint: entityBelongsToGroup
                        ? 'This entity already belongs to a group.'
                        : workpaperForm === true
                        ? disableCreateWorkpaperHint(firm, e, subscription)
                        : undefined,
                };
            }) || []
        );
    }, [entityQuery.data?.entities, selected, subscription, workpaperForm, firm]);

    useEffect(() => {
        if (!onChange || entityQuery.isLoading || entityQuery.isFetching) {
            return;
        }
        onChange(
            entityQuery.data?.entities?.filter((e) => {
                return (selected || []).find((value) => value === e.id);
            }) ?? []
        );
    }, [entityQuery.data?.entities, entityQuery.isFetching, entityQuery.isLoading, selected]);

    return (
        <AsyncSelect
            placeholder={placeholder || 'Select entities...'}
            title={title}
            form={form}
            isOptional={isOptional}
            isMulti={isMulti}
            isLoading={entityQuery.isFetching || entityQuery.isLoading}
            options={entities}
            onSearch={setEntitySearch}
            onChange={handleEntitySelect}
            isDisabled={isDisabled}
            errorMessage={errorMessage}
            ref={innerRef}
        />
    );
}
export default EntitySelect;
